import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Bot as BotIcon, Plus, Power, RefreshCw, Trash2, Copy, Check, ExternalLink } from 'lucide-react';
import { botsApi, type PublicBot } from '@/api/bots';

/**
 * Страница `/bots` — управление ботами владельца. Один UI и для списка,
 * и для редактирования (выбранный бот в правой колонке). Сырой токен
 * показывается ровно ОДИН раз: после createBot или rotateToken, в виде
 * модалки с предупреждением «больше не покажем».
 */
export default function BotsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [issuedToken, setIssuedToken] = useState<{ token: string; botId: string } | null>(null);

  const { data: bots = [], isLoading } = useQuery({
    queryKey: ['bots'],
    queryFn: () => botsApi.list(),
  });

  const selected = bots.find((b) => b.id === selectedId) ?? null;

  return (
    <div className="flex-1 flex flex-col bg-surface-950 text-surface-100">
      <div className="h-14 md:h-12 bg-surface-900 md:bg-transparent border-b border-surface-800 px-2 md:px-4 flex items-center gap-2 md:gap-3">
        <button onClick={() => navigate('/')} className="p-2 md:p-1.5 text-surface-400 rounded-full active:bg-surface-800 md:btn-ghost">
          <ArrowLeft size={20} />
        </button>
        <BotIcon size={18} className="text-brand-400" />
        <span className="font-semibold text-surface-50 md:text-surface-100 text-base md:text-sm">Боты</span>
        <button
          onClick={() => setCreating(true)}
          className="ml-auto px-3 py-1.5 rounded-md bg-brand-600 hover:bg-brand-500 text-white text-sm flex items-center gap-1"
        >
          <Plus size={14} /> Новый бот
        </button>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <aside className="w-64 border-r border-surface-800 overflow-y-auto">
          {isLoading && <div className="p-4 text-surface-500 text-sm">Загрузка…</div>}
          {!isLoading && bots.length === 0 && (
            <div className="p-4 text-surface-500 text-sm">У вас пока нет ботов</div>
          )}
          {bots.map((b) => (
            <button
              key={b.id}
              onClick={() => setSelectedId(b.id)}
              className={`w-full text-left px-4 py-3 border-b border-surface-900 hover:bg-surface-900 ${selectedId === b.id ? 'bg-surface-900' : ''}`}
            >
              <div className="text-sm font-semibold flex items-center gap-2">
                <BotIcon size={14} className="text-brand-400" />
                {b.displayName}
              </div>
              <div className="text-xs text-surface-500 font-mono">@{b.username}</div>
              {!b.isEnabled && <div className="text-[10px] text-amber-400 mt-0.5">отключён</div>}
            </button>
          ))}
        </aside>

        <main className="flex-1 overflow-y-auto p-6">
          {selected ? (
            <BotDetail
              bot={selected}
              onChange={() => qc.invalidateQueries({ queryKey: ['bots'] })}
              onTokenIssued={(token) => setIssuedToken({ token, botId: selected.id })}
              onDeleted={() => {
                setSelectedId(null);
                qc.invalidateQueries({ queryKey: ['bots'] });
              }}
            />
          ) : (
            <div className="text-surface-500 text-sm">Выберите бота слева или создайте нового</div>
          )}
        </main>
      </div>

      {creating && (
        <CreateBotModal
          onClose={() => setCreating(false)}
          onCreated={(token, botId) => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['bots'] });
            setIssuedToken({ token, botId });
            setSelectedId(botId);
          }}
        />
      )}

      {issuedToken && (
        <TokenModal token={issuedToken.token} onClose={() => setIssuedToken(null)} />
      )}
    </div>
  );
}

// ── Detail ─────────────────────────────────────────────────────────────────

function BotDetail({
  bot,
  onChange,
  onTokenIssued,
  onDeleted,
}: {
  bot: PublicBot;
  onChange: () => void;
  onTokenIssued: (token: string) => void;
  onDeleted: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [webhookUrl, setWebhookUrl] = useState('');

  const toggle = useMutation({
    mutationFn: (enabled: boolean) => botsApi.update(bot.id, { isEnabled: enabled }),
    onSuccess: onChange,
  });

  const rotate = useMutation({
    mutationFn: () => botsApi.rotateToken(bot.id),
    onSuccess: (r) => {
      onTokenIssued(r.token);
      onChange();
    },
    onError: (e: unknown) => setError((e as Error).message),
  });

  const remove = useMutation({
    mutationFn: () => botsApi.remove(bot.id),
    onSuccess: onDeleted,
  });

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h2 className="text-xl font-bold flex items-center gap-2">
          <BotIcon size={18} className="text-brand-400" />
          {bot.displayName}
          <span className="text-surface-500 text-sm font-mono">@{bot.username}</span>
        </h2>
        {bot.description && <p className="text-surface-400 text-sm mt-1">{bot.description}</p>}
      </div>

      {error && <div className="text-red-400 text-sm">{error}</div>}

      <div className="bg-surface-900 border border-surface-800 rounded-xl p-4 space-y-3">
        <h3 className="font-semibold text-sm">Состояние</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => toggle.mutate(!bot.isEnabled)}
            className={`px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5 ${
              bot.isEnabled ? 'bg-emerald-700 text-white' : 'bg-surface-700 text-surface-300'
            }`}
          >
            <Power size={12} /> {bot.isEnabled ? 'Включён' : 'Отключён'}
          </button>
          <span className="text-surface-500 text-xs">
            Создан {new Date(bot.createdAt).toLocaleString('ru-RU')}
          </span>
        </div>
      </div>

      <div className="bg-surface-900 border border-surface-800 rounded-xl p-4 space-y-3">
        <h3 className="font-semibold text-sm">Токен</h3>
        <p className="text-surface-500 text-xs">
          Сырой токен показывается только в момент выпуска. После ротации старый
          токен мгновенно перестаёт работать.
        </p>
        <button
          onClick={() => {
            if (!confirm('Старый токен перестанет работать. Продолжить?')) return;
            rotate.mutate();
          }}
          className="px-3 py-1.5 rounded-md bg-amber-700 hover:bg-amber-600 text-white text-xs flex items-center gap-1.5"
        >
          <RefreshCw size={12} /> Сгенерировать новый токен
        </button>
      </div>

      <div className="bg-surface-900 border border-surface-800 rounded-xl p-4 space-y-3">
        <h3 className="font-semibold text-sm">Webhook</h3>
        <div className="flex gap-2">
          <input
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder={bot.webhookUrl ?? 'https://example.com/hook'}
            className="flex-1 bg-surface-950 border border-surface-700 rounded-md px-3 py-1.5 text-sm"
          />
          <button
            onClick={() => alert('Установка webhook делается ботом по своему токену через POST /api/bot-api/setWebhook')}
            className="px-3 py-1.5 rounded-md bg-surface-800 text-surface-300 text-xs"
          >
            <ExternalLink size={12} className="inline mr-1" />
            Как установить
          </button>
        </div>
        {bot.webhookUrl && (
          <div className="text-xs text-surface-400">
            Текущий: <span className="font-mono break-all">{bot.webhookUrl}</span>
          </div>
        )}
        {bot.webhookLastOkAt && (
          <div className="text-xs text-emerald-400">
            Последняя успешная доставка: {new Date(bot.webhookLastOkAt).toLocaleString('ru-RU')}
          </div>
        )}
        {bot.webhookLastError && (
          <div className="text-xs text-red-400">
            Последняя ошибка: {bot.webhookLastError}
          </div>
        )}
      </div>

      <div className="bg-surface-900 border border-surface-800 rounded-xl p-4 space-y-3">
        <h3 className="font-semibold text-sm">Команды</h3>
        {bot.commands.length === 0 ? (
          <p className="text-surface-500 text-xs">
            Бот ещё не зарегистрировал команды (POST /api/bot-api/setMyCommands).
          </p>
        ) : (
          <ul className="space-y-1 text-xs">
            {bot.commands.map((c) => (
              <li key={c.command}>
                <span className="text-brand-400 font-mono">/{c.command}</span>{' '}
                <span className="text-surface-300">— {c.description}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-surface-900 border border-red-900/40 rounded-xl p-4 space-y-2">
        <h3 className="font-semibold text-sm text-red-400">Удалить бота</h3>
        <p className="text-surface-500 text-xs">
          Удаление навсегда снесёт бота, его токены, сообщения, веб-хуки и
          разрешения на DM. Действие не отменяется.
        </p>
        <button
          onClick={() => {
            if (!confirm(`Удалить бота @${bot.username}? Действие не отменяется.`)) return;
            remove.mutate();
          }}
          className="px-3 py-1.5 rounded-md bg-red-700 hover:bg-red-600 text-white text-xs flex items-center gap-1.5"
        >
          <Trash2 size={12} /> Удалить бота
        </button>
      </div>
    </div>
  );
}

// ── Create modal ───────────────────────────────────────────────────────────

function CreateBotModal({ onClose, onCreated }: { onClose: () => void; onCreated: (token: string, botId: string) => void }) {
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => botsApi.create({ username, displayName, description: description || undefined }),
    onSuccess: (r) => onCreated(r.token, r.bot.id),
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? 'Не удалось создать бота');
    },
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-surface-900 border border-surface-800 rounded-xl p-6 w-full max-w-md">
        <h3 className="text-lg font-bold mb-4">Новый бот</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-surface-400">Username (должен оканчиваться на bot)</label>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="my_bot"
              className="w-full bg-surface-950 border border-surface-700 rounded-md px-3 py-1.5 text-sm font-mono"
            />
          </div>
          <div>
            <label className="text-xs text-surface-400">Отображаемое имя</label>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Мой бот"
              className="w-full bg-surface-950 border border-surface-700 rounded-md px-3 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-surface-400">Описание (опционально)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full bg-surface-950 border border-surface-700 rounded-md px-3 py-1.5 text-sm"
            />
          </div>
        </div>
        {error && <div className="mt-3 text-red-400 text-xs">{error}</div>}
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-md bg-surface-800 text-surface-300 text-sm">
            Отмена
          </button>
          <button
            onClick={() => create.mutate()}
            disabled={!username || !displayName || create.isPending}
            className="px-3 py-1.5 rounded-md bg-brand-600 hover:bg-brand-500 text-white text-sm disabled:opacity-50"
          >
            Создать
          </button>
        </div>
      </div>
    </div>
  );
}

// ── One-time token modal ───────────────────────────────────────────────────

function TokenModal({ token, onClose }: { token: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-surface-900 border border-amber-700/40 rounded-xl p-6 w-full max-w-lg">
        <h3 className="text-lg font-bold mb-2 text-amber-400">Токен бота</h3>
        <p className="text-surface-300 text-sm mb-3">
          Сохраните этот токен прямо сейчас. Мы больше не покажем его в открытом
          виде — только хеш в БД. При утере придётся ротировать.
        </p>
        <div className="bg-surface-950 border border-surface-800 rounded-md p-3 font-mono text-xs break-all text-emerald-300">
          {token}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={() => {
              navigator.clipboard.writeText(token).catch(() => undefined);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
            className="px-3 py-1.5 rounded-md bg-surface-800 text-surface-200 text-sm flex items-center gap-1.5"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Скопировано' : 'Копировать'}
          </button>
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md bg-brand-600 hover:bg-brand-500 text-white text-sm"
          >
            Я сохранил
          </button>
        </div>
      </div>
    </div>
  );
}
